import { prepareProfileMetaContextHeaders } from '@entities/users/apis/meta-profile-context'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { createBaseResponseSchema } from '@server/presentation/schema'
import { translate, type Locale } from '@shared/config/i18n'
import { dayjs, DEFAULT_TIMEZONE } from '@shared/libs/dayjs'
import { z } from 'zod'

import type {
  TrialExperienceHomeCard,
  TrialExperienceHomeCardCta,
  TrialExperienceHomeCardDiscountBadge,
  TrialExperienceHomeCardFallback,
  TrialExperienceHomeCardLessonPreview,
  TrialExperienceHomeCardPreparationItem,
  TrialExperienceHomeCardReservation,
  TrialExperienceHomeCardType,
  TrialExperienceHomeState,
} from './home-card'
import { ONBOARDING_LEVEL_LABEL } from './level-labels'
import type { OnboardingBookingLanguage, OnboardingPreferredTime } from './schema'

export type TrialBookingLanguage = OnboardingBookingLanguage
type TrialBookingLanguageCode = 'EN' | 'JP' | 'KR'

const onboardingBookingOptionsRequestSchema = z.object({
  language: z.enum(['EN', 'JP', 'KR']),
  selectedLevel: z.string(),
  preferredTimes: z.array(z.string()),
  timezoneCode: z.string(),
  locale: z.enum(['ko', 'en', 'ja']),
  bookingFlow: z.enum(['TRIAL_APPLICATION']).optional(),
})

const onboardingBookingOptionsDataSchema = z.object({
  selected_level: z.string(),
  selected_level_description: z.string(),
  dates: z.array(
    z.object({
      day: z.string(),
      date: z.number(),
      full_date: z.string(),
    }),
  ),
  time_groups_by_date: z.record(
    z.array(
      z.object({
        group_name: z.string(),
        times: z.array(
          z.object({
            time: z.string(),
            status: z.enum(['open', 'close', 'penalty', 'holding', 'too-soon']),
            utc_class_date_time: z.string(),
          }),
        ),
      }),
    ),
  ),
  recommended_slots: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      subtitle: z.string(),
      full_date: z.string(),
      time: z.string(),
      utc_class_date_time: z.string(),
    }),
  ),
  range_expanded: z.boolean().optional().default(false),
})

const onboardingBookingOptionsResponseSchema = createBaseResponseSchema(onboardingBookingOptionsDataSchema)
const inflightBookingOptionsRequests = new Map<string, ReturnType<typeof createBookingOptionsRequestPromise>>()

const SERVER_MORNING_GROUP_NAME_PATTERN = /^오전$/
const SERVER_AFTERNOON_GROUP_NAME_PATTERN = /^오후$/
const getRelativeDayLabel = (locale: Locale, day: 'today' | 'tomorrow') => translate(locale, `booking.date.${day}`)
const getDayPeriodLabel = (locale: Locale, period: 'morning' | 'afternoon') =>
  translate(locale, `booking.dayPeriod.${period}`)
const getShortDateFormat = (locale: Locale) => translate(locale, 'onboarding.flow.booking.dateFormat')

const onboardingBookingConfirmRequestSchema = z.object({
  language: z.enum(['EN', 'JP', 'KR']),
  selectedLevel: z.string(),
  utcClassDateTime: z.string(),
  idempotencyKey: z.string().min(1).max(100).optional(),
  bookingFlow: z.enum(['TRIAL_APPLICATION']).optional(),
  subscribeId: z.string().optional(),
  productId: z.string().min(1).optional(),
  catalogProtocol: z.literal('KR_PRODUCT_V1').optional(),
})

const onboardingBookingChangeRequestSchema = onboardingBookingConfirmRequestSchema.extend({
  previousClassId: z.number().int(),
})

const onboardingBookingConfirmDataSchema = z.object({
  selected_class_id: z.number(),
  prestudy_class_id: z.number().nullable().optional(),
  selected_class_datetime: z.string(),
  trial_booked: z.boolean(),
  ticket_provisioned: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false),
})

const onboardingBookingConfirmResponseSchema = createBaseResponseSchema(onboardingBookingConfirmDataSchema)

const onboardingMarketingConsentRequestSchema = z.object({
  marketingAgreed: z.boolean(),
  language: z.enum(['EN', 'JP', 'KR']).optional(),
  requiredTermsAgreed: z.boolean().optional(),
  termsDocumentVersion: z.string().min(1).max(100).optional(),
})

const onboardingMarketingConsentDataSchema = z.object({
  marketing_agreed: z.boolean(),
  coupon_issued: z.boolean(),
  coupon_id: z.string().nullable().optional(),
})

const onboardingMarketingConsentResponseSchema = createBaseResponseSchema(onboardingMarketingConsentDataSchema)

const onboardingStateDataSchema = z.object({
  entry_source: z.string(),
  language: z.enum(['EN', 'JP', 'KR']).nullable(),
  goals: z.array(z.string()),
  experience_level: z.number().nullable(),
  frequency: z.number().nullable(),
  preferred_times: z.array(z.string()),
  class_styles: z.array(z.string()),
  recommended_level: z.string().nullable(),
  selected_level: z.string().nullable(),
  style_tags: z.array(z.string()),
  lesson_style_tags: z.array(z.string()),
  selected_class_id: z.number().nullable(),
  prestudy_class_id: z.number().nullable().optional(),
  selected_class_datetime: z.string().nullable().optional(),
  trial_booked: z.boolean(),
  trial_skipped: z.boolean(),
  marketing_agreed: z.boolean(),
  notification_responded: z.boolean(),
  completed: z.boolean(),
})

export const onboardingStateResponseSchema = createBaseResponseSchema(onboardingStateDataSchema)

const trialExperienceStateDataSchema = z
  .object({
    state: z.string().nullable().optional(),
    reason: z.string().nullable().optional(),
    language: z.enum(['EN', 'JP', 'KR']).nullable().optional(),
    class_id: z.number().nullable().optional(),
    classId: z.number().nullable().optional(),
    prestudy_class_id: z.number().nullable().optional(),
    prestudyClassId: z.number().nullable().optional(),
    class_ticket_id: z.string().nullable().optional(),
    classTicketId: z.string().nullable().optional(),
    class_course_id: z.number().nullable().optional(),
    classCourseId: z.number().nullable().optional(),
    trial_level: z.string().nullable().optional(),
    trialLevel: z.string().nullable().optional(),
    class_start_at: z.string().nullable().optional(),
    classStartAt: z.string().nullable().optional(),
    no_show_count: z.number().nullable().optional(),
    noShowCount: z.number().nullable().optional(),
    available_actions: z.array(z.string()).optional(),
    availableActions: z.array(z.string()).optional(),
    has_unbooked_trial_ticket: z.boolean().optional(),
    hasUnbookedTrialTicket: z.boolean().optional(),
    paid_trial: z.boolean().nullable().optional(),
    paidTrial: z.boolean().nullable().optional(),
    booking_class_id: z.number().nullable().optional(),
    bookingClassId: z.number().nullable().optional(),
    home_card: z.record(z.unknown()).nullable().optional(),
    homeCard: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough()

const trialExperienceStateResponseSchema = createBaseResponseSchema(trialExperienceStateDataSchema)

export const normalizeLanguage = (language: TrialBookingLanguage): TrialBookingLanguageCode => {
  switch (language) {
    case 'en':
      return 'EN'
    case 'ko':
      return 'KR'
    case 'ja':
      return 'JP'
  }
}

const getLegacySelectedLevelForBooking = (language: TrialBookingLanguageCode, selectedLevel: string) => {
  if (language === 'EN' && selectedLevel === ONBOARDING_LEVEL_LABEL.preAdvanced) {
    return ONBOARDING_LEVEL_LABEL.intermediate
  }

  return null
}

const TRIAL_LEVEL_NOT_FOUND_MESSAGE_PATTERN = /레벨 정보를 찾을 수 없습니다/

const isTrialLevelNotFoundError = (error: unknown) => {
  const maybeError = error as {
    code?: unknown
    status?: unknown
    cause?: { resultCdName?: unknown; message?: unknown }
    message?: unknown
  }

  return (
    maybeError.code === 'TRIAL_LEVEL_NOT_FOUND' ||
    maybeError.cause?.resultCdName === 'TRIAL_LEVEL_NOT_FOUND' ||
    (maybeError.status === 404 &&
      typeof maybeError.cause?.message === 'string' &&
      TRIAL_LEVEL_NOT_FOUND_MESSAGE_PATTERN.test(maybeError.cause.message)) ||
    (maybeError.status === 404 &&
      typeof maybeError.message === 'string' &&
      TRIAL_LEVEL_NOT_FOUND_MESSAGE_PATTERN.test(maybeError.message))
  )
}

export const isOnboardingStateNotFoundError = (error: unknown) => {
  const maybeError = error as {
    code?: unknown
    status?: unknown
    cause?: { resultCd?: unknown; resultCdName?: unknown; message?: unknown }
  }

  return (
    maybeError.status === 404 ||
    maybeError.code === 'ONBOARDING_STATE_NOT_FOUND' ||
    maybeError.cause?.resultCdName === 'ONBOARDING_STATE_NOT_FOUND' ||
    maybeError.cause?.resultCd === '404' ||
    maybeError.cause?.resultCd === 404
  )
}

export const mapOnboardingStateResponse = (parsed: z.infer<typeof onboardingStateDataSchema>) => ({
  entrySource: parsed.entry_source,
  language: parsed.language,
  goals: parsed.goals,
  experienceLevel: parsed.experience_level,
  frequency: parsed.frequency,
  preferredTimes: parsed.preferred_times,
  classStyles: parsed.class_styles,
  recommendedLevel: parsed.recommended_level,
  selectedLevel: parsed.selected_level,
  styleTags: parsed.style_tags,
  lessonStyleTags: parsed.lesson_style_tags,
  selectedClassId: parsed.selected_class_id,
  prestudyClassId: parsed.prestudy_class_id ?? null,
  selectedClassDatetime: parsed.selected_class_datetime,
  trialBooked: parsed.trial_booked,
  trialSkipped: parsed.trial_skipped,
  marketingAgreed: parsed.marketing_agreed,
  notificationResponded: parsed.notification_responded,
  completed: parsed.completed,
})

const TRIAL_EXPERIENCE_HOME_CARD_TYPES = [
  'NOT_RESERVED',
  'RESERVED_WAITING',
  'FIRST_NOSHOW',
  'COMPLETED',
  'DOUBLE_NOSHOW',
  'UNKNOWN',
] as const

const toRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null

const readValue = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (key in source) {
      return source[key]
    }
  }

  return undefined
}

const readString = (source: Record<string, unknown>, ...keys: string[]): string | null => {
  const value = readValue(source, ...keys)
  return typeof value === 'string' ? value : null
}

const readNumber = (source: Record<string, unknown>, ...keys: string[]): number | null => {
  const value = readValue(source, ...keys)
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : null
  }

  return null
}

const readBoolean = (source: Record<string, unknown>, key: string, fallback = false): boolean => {
  const value = readValue(source, key)
  return typeof value === 'boolean' ? value : fallback
}

const toTrialExperienceHomeCardType = (value: unknown): TrialExperienceHomeCardType => {
  if (typeof value === 'string' && TRIAL_EXPERIENCE_HOME_CARD_TYPES.includes(value as TrialExperienceHomeCardType)) {
    return value as TrialExperienceHomeCardType
  }

  return 'UNKNOWN'
}

const mapTrialExperienceCta = (raw: unknown): TrialExperienceHomeCardCta | null => {
  const source = toRecord(raw)
  if (!source) {
    return null
  }

  const label = readString(source, 'label')
  const action = readString(source, 'action')
  if (!label || !action) {
    return null
  }

  return {
    label,
    action,
    target: readString(source, 'target'),
    url: readString(source, 'url'),
    enabled: readBoolean(source, 'enabled', true),
  }
}

const mapTrialExperienceReservation = (raw: unknown): TrialExperienceHomeCardReservation | null => {
  const source = toRecord(raw)
  if (!source) {
    return null
  }

  return {
    classId: readNumber(source, 'classId', 'class_id'),
    prestudyClassId: readNumber(source, 'prestudyClassId', 'prestudy_class_id'),
    classStartAt: readString(source, 'classStartAt', 'class_start_at'),
    classEndAt: readString(source, 'classEndAt', 'class_end_at'),
    dDay: readNumber(source, 'dDay'),
    lessonName: readString(source, 'lessonName', 'lesson_name'),
    tutorId: readNumber(source, 'tutorId', 'tutor_id'),
    tutorName: readString(source, 'tutorName', 'tutor_name'),
  }
}

const mapTrialExperienceLessonPreview = (raw: unknown): TrialExperienceHomeCardLessonPreview | null => {
  const source = toRecord(raw)
  if (!source) {
    return null
  }

  return {
    classCourseId: readNumber(source, 'classCourseId', 'class_course_id'),
    title: readString(source, 'title'),
    classLevel: readNumber(source, 'classLevel', 'class_level'),
    classWeek: readNumber(source, 'classWeek', 'class_week'),
    curriculumType: readString(source, 'curriculumType', 'curriculum_type'),
    lessonTime: readNumber(source, 'lessonTime', 'lesson_time'),
    thumbnailUrl: readString(source, 'thumbnailUrl', 'thumbnail_url'),
    description: readString(source, 'description'),
  }
}

const mapTrialExperienceDiscountBadge = (raw: unknown): TrialExperienceHomeCardDiscountBadge | null => {
  const source = toRecord(raw)
  if (!source) {
    return null
  }

  return {
    visible: readBoolean(source, 'visible'),
    label: readString(source, 'label'),
    source: readString(source, 'source'),
    fallback: readBoolean(source, 'fallback'),
  }
}

const mapTrialExperiencePreparationItems = (raw: unknown): TrialExperienceHomeCardPreparationItem[] => {
  if (!Array.isArray(raw)) {
    return []
  }

  return raw.flatMap((item) => {
    const source = toRecord(item)
    if (!source) {
      return []
    }

    const type = readString(source, 'type')
    const label = readString(source, 'label')
    const action = readString(source, 'action')
    if (!type || !label || !action) {
      return []
    }

    return {
      type,
      label,
      action,
      url: readString(source, 'url'),
      enabled: readBoolean(source, 'enabled', true),
    }
  })
}

const mapTrialExperienceFallback = (raw: unknown): TrialExperienceHomeCardFallback | null => {
  const source = toRecord(raw)
  if (!source) {
    return null
  }

  return {
    enabled: readBoolean(source, 'enabled'),
    reason: readString(source, 'reason'),
    message: readString(source, 'message'),
  }
}

const mapTrialExperienceHomeCard = (raw: unknown): TrialExperienceHomeCard | null => {
  const source = toRecord(raw)
  if (!source) {
    return null
  }

  const cardType = toTrialExperienceHomeCardType(readValue(source, 'cardType', 'card_type'))
  const title = readString(source, 'title')
  if (!title) {
    return null
  }

  return {
    cardType,
    title,
    description: readString(source, 'description'),
    primaryCta: mapTrialExperienceCta(readValue(source, 'primaryCta', 'primary_cta')),
    secondaryCta: mapTrialExperienceCta(readValue(source, 'secondaryCta', 'secondary_cta')),
    reservation: mapTrialExperienceReservation(source.reservation),
    lessonPreview: mapTrialExperienceLessonPreview(readValue(source, 'lessonPreview', 'lesson_preview')),
    discountBadge: mapTrialExperienceDiscountBadge(readValue(source, 'discountBadge', 'discount_badge')),
    preparationItems: mapTrialExperiencePreparationItems(readValue(source, 'preparationItems', 'preparation_items')),
    fallback: mapTrialExperienceFallback(source.fallback),
  }
}

const mapTrialExperienceStateResponse = (
  parsed: z.infer<typeof trialExperienceStateDataSchema>,
): TrialExperienceHomeState => ({
  state: toTrialExperienceHomeCardType(parsed.state),
  reason: parsed.reason ?? null,
  language: parsed.language ?? null,
  classId: parsed.class_id ?? parsed.classId ?? null,
  prestudyClassId: parsed.prestudy_class_id ?? parsed.prestudyClassId ?? null,
  classTicketId: parsed.class_ticket_id ?? parsed.classTicketId ?? null,
  classCourseId: parsed.class_course_id ?? parsed.classCourseId ?? null,
  trialLevel: parsed.trial_level ?? parsed.trialLevel ?? null,
  classStartAt: parsed.class_start_at ?? parsed.classStartAt ?? null,
  noShowCount: parsed.no_show_count ?? parsed.noShowCount ?? 0,
  availableActions: parsed.available_actions ?? parsed.availableActions ?? [],
  hasUnbookedTrialTicket: parsed.has_unbooked_trial_ticket ?? parsed.hasUnbookedTrialTicket ?? false,
  paidTrial: parsed.paid_trial ?? parsed.paidTrial ?? null,
  bookingClassId: parsed.booking_class_id ?? parsed.bookingClassId ?? null,
  homeCard: mapTrialExperienceHomeCard(parsed.home_card ?? parsed.homeCard),
})

export const requestOnboardingBookingOptions = async (
  params: {
    language: TrialBookingLanguage
    selectedLevel: string
    preferredTime: OnboardingPreferredTime[]
    timezoneCode: string
    locale: Locale
    bookingFlow?: 'TRIAL_APPLICATION'
  },
  bearerToken: string,
) => {
  const payload = onboardingBookingOptionsRequestSchema.parse({
    language: normalizeLanguage(params.language),
    selectedLevel: params.selectedLevel,
    preferredTimes: params.preferredTime,
    timezoneCode: params.timezoneCode,
    locale: params.locale,
    bookingFlow: params.bookingFlow,
  })
  // Requests and their conversion context belong to this authenticated account, not just the slot filters.
  const requestKey = JSON.stringify([bearerToken, payload])
  const existingRequest = inflightBookingOptionsRequests.get(requestKey)
  if (existingRequest) {
    return existingRequest
  }

  const requestPromise = createBookingOptionsRequestPromiseWithLevelFallback(
    payload,
    params.timezoneCode,
    params.locale,
    bearerToken,
  )
  inflightBookingOptionsRequests.set(requestKey, requestPromise)

  try {
    return await requestPromise
  } finally {
    inflightBookingOptionsRequests.delete(requestKey)
  }
}

const createBookingOptionsRequestPromise = async (
  payload: z.infer<typeof onboardingBookingOptionsRequestSchema>,
  timezoneCode: string,
  locale: Locale,
  bearerToken: string,
  selectedLevelOverride?: string,
) => {
  const response = await podoFetcher<z.infer<typeof onboardingBookingOptionsResponseSchema>>({
    method: 'POST',
    url: '/api/v1/onboarding/booking/options',
    body: payload,
    header: {
      Authorization: `Bearer ${bearerToken}`,
      // The legacy options path can commit a new free entitlement before a separate confirm.
      ...(payload.bookingFlow === 'TRIAL_APPLICATION' ? {} : await prepareProfileMetaContextHeaders(bearerToken)),
    },
  })

  const parsed = onboardingBookingOptionsResponseSchema.parse(response).data

  const formatDisplaySlot = (utcClassDateTime: string) => {
    const localDateTime = dayjs.utc(utcClassDateTime).tz(timezoneCode || DEFAULT_TIMEZONE)
    const today = dayjs()
      .tz(timezoneCode || DEFAULT_TIMEZONE)
      .startOf('day')

    const fullDate = localDateTime.format('YYYY-MM-DD')
    const time = localDateTime.format('HH:mm')

    let label: string
    if (localDateTime.isSame(today, 'day')) {
      label = `${getRelativeDayLabel(locale, 'today')} ${time}`
    } else if (localDateTime.isSame(today.add(1, 'day'), 'day')) {
      label = `${getRelativeDayLabel(locale, 'tomorrow')} ${time}`
    } else {
      label = `${localDateTime.locale(locale).format(getShortDateFormat(locale))} ${time}`
    }

    return {
      label,
      subtitle: fullDate,
      fullDate,
      time,
    }
  }

  const formatDateItemDay = (fullDate: string, fallback: string) => {
    const localDate = dayjs.tz(fullDate, timezoneCode || DEFAULT_TIMEZONE)
    const today = dayjs()
      .tz(timezoneCode || DEFAULT_TIMEZONE)
      .startOf('day')

    if (localDate.isSame(today, 'day')) {
      return getRelativeDayLabel(locale, 'today')
    }
    if (localDate.isSame(today.add(1, 'day'), 'day')) {
      return getRelativeDayLabel(locale, 'tomorrow')
    }
    return localDate.locale(locale).format('ddd') || fallback
  }

  const formatGroupName = (groupName: string, times: { time: string }[]) => {
    if (SERVER_MORNING_GROUP_NAME_PATTERN.test(groupName)) {
      return getDayPeriodLabel(locale, 'morning')
    }
    if (SERVER_AFTERNOON_GROUP_NAME_PATTERN.test(groupName)) {
      return getDayPeriodLabel(locale, 'afternoon')
    }

    const firstTime = times[0]?.time
    if (!firstTime) {
      return groupName
    }

    return Number(firstTime.split(':')[0]) < 12
      ? getDayPeriodLabel(locale, 'morning')
      : getDayPeriodLabel(locale, 'afternoon')
  }

  return {
    selectedLevel: selectedLevelOverride ?? parsed.selected_level,
    selectedLevelDescription: parsed.selected_level_description,
    dates: parsed.dates.map((dateItem) => ({
      day: formatDateItemDay(dateItem.full_date, dateItem.day),
      date: dateItem.date,
      fullDate: dateItem.full_date,
    })),
    recommendedSlots: parsed.recommended_slots.map((slot) => ({
      ...slot,
      utcClassDateTime: slot.utc_class_date_time,
      ...formatDisplaySlot(slot.utc_class_date_time),
    })),
    timeGroupsByDate: Object.fromEntries(
      Object.entries(parsed.time_groups_by_date).map(([dateKey, groups]) => [
        dateKey,
        groups.map((group) => {
          const times = group.times.map((timeSlot) => ({
            time: dayjs
              .utc(timeSlot.utc_class_date_time)
              .tz(timezoneCode || DEFAULT_TIMEZONE)
              .format('HH:mm'),
            status: timeSlot.status,
            utcClassDateTime: timeSlot.utc_class_date_time,
          }))

          return {
            groupName: formatGroupName(group.group_name, times),
            times,
          }
        }),
      ]),
    ),
    rangeExpanded: parsed.range_expanded,
  }
}

const createBookingOptionsRequestPromiseWithLevelFallback = async (
  payload: z.infer<typeof onboardingBookingOptionsRequestSchema>,
  timezoneCode: string,
  locale: Locale,
  bearerToken: string,
) => {
  try {
    return await createBookingOptionsRequestPromise(payload, timezoneCode, locale, bearerToken)
  } catch (error) {
    const legacySelectedLevel = getLegacySelectedLevelForBooking(payload.language, payload.selectedLevel)

    if (!legacySelectedLevel || !isTrialLevelNotFoundError(error)) {
      throw error
    }

    return createBookingOptionsRequestPromise(
      { ...payload, selectedLevel: legacySelectedLevel },
      timezoneCode,
      locale,
      bearerToken,
      payload.selectedLevel,
    )
  }
}

export const requestOnboardingState = async (bearerToken: string, language?: TrialBookingLanguage) => {
  const normalizedLanguage = language ? normalizeLanguage(language) : undefined
  const query = normalizedLanguage ? `?language=${normalizedLanguage}` : ''
  const response = await podoFetcher<z.infer<typeof onboardingStateResponseSchema>>({
    method: 'GET',
    url: `/api/v1/onboarding/state${query}`,
    header: {
      Authorization: `Bearer ${bearerToken}`,
    },
  })

  const parsed = onboardingStateResponseSchema.parse(response).data

  return mapOnboardingStateResponse(parsed)
}

export const requestTrialExperienceState = async (
  bearerToken: string,
  language?: TrialBookingLanguage,
  options?: { timeoutMs?: number },
) => {
  const normalizedLanguage = language ? normalizeLanguage(language) : undefined
  const query = normalizedLanguage ? `?language=${normalizedLanguage}` : ''
  const response = await podoFetcher<z.infer<typeof trialExperienceStateResponseSchema>>(
    {
      method: 'GET',
      url: `/api/v1/onboarding/trial-experience-state${query}`,
      header: {
        Authorization: `Bearer ${bearerToken}`,
      },
    },
    options,
  )

  const parsed = trialExperienceStateResponseSchema.parse(response).data

  return mapTrialExperienceStateResponse(parsed)
}

export type OnboardingBookingConfirmResult = {
  selectedClassId: number
  prestudyClassId: number | null
  selectedClassDatetime: string
  trialBooked: boolean
  ticketProvisioned?: boolean
}

export const requestOnboardingBookingConfirm = async (
  params: {
    language: TrialBookingLanguage
    selectedLevel: string
    utcClassDateTime: string
    idempotencyKey?: string
    bookingFlow?: 'TRIAL_APPLICATION'
    subscribeId?: string
    productId?: string
    catalogProtocol?: 'KR_PRODUCT_V1'
  },
  bearerToken: string,
): Promise<OnboardingBookingConfirmResult> => {
  const payload = onboardingBookingConfirmRequestSchema.parse({
    language: normalizeLanguage(params.language),
    selectedLevel: params.selectedLevel,
    utcClassDateTime: params.utcClassDateTime,
    idempotencyKey: params.idempotencyKey,
    bookingFlow: params.bookingFlow,
    subscribeId: params.subscribeId,
    productId: params.productId,
    catalogProtocol: params.catalogProtocol,
  })

  const metaHeaders = await prepareProfileMetaContextHeaders(bearerToken)
  const postConfirm = (requestPayload: typeof payload) =>
    podoFetcher<z.infer<typeof onboardingBookingConfirmResponseSchema>>({
      method: 'POST',
      url: '/api/v1/onboarding/booking/confirm',
      body: requestPayload,
      header: {
        ...metaHeaders,
        Authorization: `Bearer ${bearerToken}`,
      },
    })

  let response: z.infer<typeof onboardingBookingConfirmResponseSchema>

  try {
    response = await postConfirm(payload)
  } catch (error) {
    const legacySelectedLevel = getLegacySelectedLevelForBooking(payload.language, payload.selectedLevel)

    if (!legacySelectedLevel || !isTrialLevelNotFoundError(error)) {
      throw error
    }

    response = await postConfirm({ ...payload, selectedLevel: legacySelectedLevel })
  }

  const parsed = onboardingBookingConfirmResponseSchema.parse(response).data

  return {
    selectedClassId: parsed.selected_class_id,
    prestudyClassId: parsed.prestudy_class_id ?? null,
    selectedClassDatetime: parsed.selected_class_datetime,
    trialBooked: parsed.trial_booked,
    ticketProvisioned: parsed.ticket_provisioned,
  }
}

export const requestOnboardingBookingChange = async (
  params: {
    language: TrialBookingLanguage
    selectedLevel: string
    utcClassDateTime: string
    previousClassId: number
  },
  bearerToken: string,
) => {
  const payload = onboardingBookingChangeRequestSchema.parse({
    language: normalizeLanguage(params.language),
    selectedLevel: params.selectedLevel,
    utcClassDateTime: params.utcClassDateTime,
    previousClassId: params.previousClassId,
  })

  const postChange = (requestPayload: typeof payload) =>
    podoFetcher<z.infer<typeof onboardingBookingConfirmResponseSchema>>({
      method: 'POST',
      url: '/api/v1/onboarding/booking/change',
      body: requestPayload,
      header: {
        Authorization: `Bearer ${bearerToken}`,
      },
    })

  let response: z.infer<typeof onboardingBookingConfirmResponseSchema>

  try {
    response = await postChange(payload)
  } catch (error) {
    const legacySelectedLevel = getLegacySelectedLevelForBooking(payload.language, payload.selectedLevel)

    if (!legacySelectedLevel || !isTrialLevelNotFoundError(error)) {
      throw error
    }

    response = await postChange({ ...payload, selectedLevel: legacySelectedLevel })
  }

  const parsed = onboardingBookingConfirmResponseSchema.parse(response).data

  return {
    selectedClassId: parsed.selected_class_id,
    prestudyClassId: parsed.prestudy_class_id ?? null,
    selectedClassDatetime: parsed.selected_class_datetime,
    trialBooked: parsed.trial_booked,
  }
}

export const requestOnboardingMarketingConsent = async (
  params: {
    marketingAgreed: boolean
    language?: TrialBookingLanguage
    requiredTermsAgreed?: boolean
    termsDocumentVersion?: string
  },
  bearerToken: string,
) => {
  const payload = onboardingMarketingConsentRequestSchema.parse({
    ...params,
    language: params.language ? normalizeLanguage(params.language) : undefined,
  })

  const response = await podoFetcher<z.infer<typeof onboardingMarketingConsentResponseSchema>>({
    method: 'POST',
    url: '/api/v1/onboarding/marketing-consent',
    body: payload,
    header: {
      Authorization: `Bearer ${bearerToken}`,
    },
  })

  const parsed = onboardingMarketingConsentResponseSchema.parse(response).data

  return {
    marketingAgreed: parsed.marketing_agreed,
    couponIssued: parsed.coupon_issued,
    couponId: parsed.coupon_id ?? null,
  }
}
